
import { supabase } from "@/lib/supabase";

/**
 * Finds an existing profile or creates a new one if it doesn't exist
 * @param email The user's email address
 * @param fullName The user's full name (optional for existing users)
 * @returns The user's UUID from the profiles table
 */
export const findOrCreateProfile = async (email: string, fullName: string = ''): Promise<string> => {
  try {
    if (!email) {
      throw new Error("Email is required to find or create a profile");
    }
    
    // First, try to find an existing profile
    const { data: existingProfile, error: findError } = await supabase
      .from('profiles')
      .select('id')
      .eq('email', email)
      .maybeSingle();
      
    if (findError) {
      console.error("Error finding profile:", findError);
      throw findError;
    }
    
    // If a profile exists, return its ID
    if (existingProfile?.id) {
      // If fullName is provided and different from stored, update it
      if (fullName) {
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ full_name: fullName, updated_at: new Date().toISOString() })
          .eq('id', existingProfile.id);
          
        if (updateError) {
          console.error("Error updating profile name:", updateError);
          // Continue anyway, not critical
        }
      }
      
      return existingProfile.id;
    }
    
    // No existing profile found, create a new one
    // Use a direct RPC call to bypass RLS
    const newProfileId = crypto.randomUUID();
    
    // Call the create_profile function that we created in the database
    const { data, error: createError } = await supabase.rpc('create_profile', {
      p_id: newProfileId,
      p_email: email,
      p_full_name: fullName
    });
    
    if (createError) {
      console.error("Error creating profile via RPC:", createError);
      throw createError;
    }
    
    return newProfileId;
  } catch (error) {
    console.error("Error in findOrCreateProfile:", error);
    throw error;
  }
};

/**
 * Gets a user's profile by their email address
 * @param email The user's email address
 * @returns The user's profile object or null if not found
 */
export const getUserProfileByEmail = async (email: string) => {
  try {
    if (!email) return null;
    
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('email', email)
      .maybeSingle();
      
    if (error) {
      console.error("Error fetching user profile:", error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error("Error in getUserProfileByEmail:", error);
    return null;
  }
};
